import { Button, Checkbox, Tooltip } from "antd";
import dayjs from "dayjs";
import { t } from "i18next";
import { useSelector } from "react-redux";

import CommonIcons from "../../../assets/icons";
import { LocalStorageKey } from "../../../constants/localStorageKeys";
import { RootState } from "../../../store/store";
import Thumbnail from "./ThumbnailToken";
import { getThumbnail } from "./ultis";
import { memo } from "react";

const CardSearchResult = ({
   item,
   providerName,
   setSelectedImages,
   selectedImages,
   handleViewInfo,
   handleShowFootPrint,
   handleMouseMoveItem,
   handleMouseLeaveItem,
   handleRemoveFootPrint,
   panTo,
   handleAddCart,
   handleRemoveFromCart,
   handleShowImageOnMap,
   handleRemoveImage,
   key
}: any) =>
{
   /* ---------------- STATIC CONFIG ---------------- */
   const staticConfigs = JSON.parse(
      localStorage.getItem(LocalStorageKey.STATIC_CONFIG) ?? "{}",
   );

   const baseUrl = staticConfigs?.REACT_APP_API_BASE_URL;

   /* ---------------- REDUX ---------------- */

   const idsImageInCart = useSelector(
      (state: RootState) => state.cartSlice.arrIdImage,
   );

   const layersOnMap = useSelector((state: RootState) => state.layer.layers);

   /* ---------------- DERIVED DATA ---------------- */

   // const providerImageId = item?.properties?.[`${providerName}:id`];

   const providerImageId = item?.id;

   const footprintLayerId = `footerprint-${item?.id}`;
   const imageLayerId = `image-${item?.id}`;

   const isSelected = selectedImages?.some((img: any) => img?.id === item?.id);
   const isShowFootPrint = layersOnMap?.some(
      (l: any) => l.id === footprintLayerId,
   );
   const isShowImage = layersOnMap?.some((l: any) => l.id === imageLayerId);
   const isInCart = idsImageInCart?.includes(providerImageId);

   /* ---------------- ACTIONS ---------------- */

   const toggleSelect = () =>
   {
      setSelectedImages((prev: any[]) =>
      {
         const existed = prev?.some((img) => img?.id === item?.id);

         if (existed)
         {
            return prev.filter((img) => img?.id !== item?.id);
         }
         return [...prev, item];
      });
   };

   const toggleCart = (e: any) =>
   {
      e.stopPropagation();

      if (isInCart)
      {
         handleRemoveFromCart([providerImageId]);
      } else
      {
         handleAddCart([providerImageId]);
      }
   };

   const toggleImageOnMap = (e: any) =>
   {
      e.stopPropagation();

      if (isShowImage)
      {
         handleRemoveImage(item);
      } else
      {
         handleShowImageOnMap(item);
      }
   };

   const toggleFootprint = (e: any) =>
   {
      e.stopPropagation();

      if (isShowFootPrint)
      {
         handleRemoveFootPrint(item);
      } else
      {
         handleShowFootPrint(item);
      }
   };

   /* ---------------- RENDER ---------------- */

   return (
      <div
         // onMouseMove={() => handleMouseMoveItem(item)}
         // onMouseLeave={() => handleMouseLeaveItem(item)}

         key={key}
         onClick={(e) =>
         {
            if (!(e.target instanceof HTMLElement)) return;

            if (e.target.closest(".ant-checkbox"))
            {
               return;
            }

            toggleSelect();
         }}
         className={`item-card-search ${isSelected ? "selected" : ""}`}
         style={{ marginBottom: '24px' }}
      >
         {/* ================== CONTENT ================== */}

         <div className="flex gap-4">
            {/* THUMBNAIL */}
            <div className="w-[80px] h-[80px] rounded bg-[#0b1323] relative">
               <Thumbnail
                  url={getThumbnail(item?.assets, baseUrl)}
                  token={localStorage.getItem("token") ?? ""}
               />

               <div className="absolute top-1 left-1">
                  <Checkbox
                     checked={isSelected}
                     onClick={(e) => e.stopPropagation()}
                     onChange={(e) =>
                     {
                        e.stopPropagation();
                        toggleSelect();
                     }}
                  />
               </div>
            </div>

            {/* INFO */}

            <div className="flex-1">
               <div className="text-xs text-gray-400">
                  {item?.collection ?? item.properties?.platform}
               </div>

               <div className="text-sm font-medium mb-2">{item?.id}</div>

               <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-300">
                  <div className="flex gap-2">
                     <span className="flex gap-1">
                        <CommonIcons.DatetimeIcon /> {t("Date time")}:
                     </span>
                     <span className="text-[var(--color-main-text)]">
                        {item.properties?.datetime ? dayjs(item.properties?.datetime).format("DD/MM/YYYY") : "---"}
                     </span>
                  </div>

                  <div className="flex gap-2">
                     <span className="flex gap-1">
                        <CommonIcons.CloudIcon /> {t("Cloud cover")}:
                     </span>
                     <span className="text-[var(--color-main-text)]">
                        {item.properties?.["eo:cloud_cover"] ? item.properties?.["eo:cloud_cover"]?.toFixed(2) : "---"}%
                     </span>
                  </div>

                  <div className="flex gap-2">
                     <span className="flex gap-1">
                        <CommonIcons.CloudIcon /> {t("Incidence angle")}:
                     </span>
                     <span className="text-[var(--color-main-text)]">
                        {item.properties?.["view:off_nadir"] ? item.properties?.["view:off_nadir"]?.toFixed(2) : "---"}
                     </span>
                  </div>

                  <div className="flex gap-2">
                     <span className="flex gap-1">
                        <CommonIcons.GsdIcon /> {t("Resolution")}:
                     </span>
                     <span className="text-[var(--color-main-text)]">
                        {item.properties?.gsd ? item.properties?.gsd?.toFixed(2) : "---"}
                     </span>
                  </div>
               </div>
            </div>
         </div>

         {/* ================== ACTIONS ================== */}

         <div className="flex justify-between items-center mt-4 pt-3 border-t border-[var(--divider-2)]">
            <Button
               type={isInCart ? "default" : "primary"}
               icon={<CommonIcons.CartMenuIcon />}
               className={
                  isInCart
                     ? "bg-gray-200 text-black border-none"
                     : "btn-primay-card-item-search"
               }
               onClick={toggleCart}
            >
               {isInCart ? t("Remove from cart") : t("Add to cart")}
            </Button>

            <div className="flex gap-2">
               <Tooltip title={t("Information")}>
                  <Button
                     className="btn-action"
                     icon={<CommonIcons.InfoCircleIcon />}
                     onClick={(e) =>
                     {
                        e.stopPropagation();
                        handleViewInfo(item);
                     }}
                  />
               </Tooltip>

               <Tooltip
                  title={isShowImage ? t("Hide image on map") : t("View on map")}
               >
                  <Button
                     onClick={toggleImageOnMap}
                     className={`btn-action ${isShowImage ? "!bg-[#4CC2FF] !text-black" : ""}`}
                     icon={<CommonIcons.MapIcon />}
                  />
               </Tooltip>

               <Tooltip
                  title={
                     isShowFootPrint
                        ? t("Hide footprint's image on map")
                        : t("Show footprint's image on map")
                  }
               >
                  <Button
                     onClick={(e) => toggleFootprint(e)}
                     className={`btn-action ${isShowFootPrint ? "!bg-[#4CC2FF] !text-black" : ""}`}
                     icon={<CommonIcons.FootIcon />}
                  />
               </Tooltip>

               <Tooltip title={t("Pan to")}>
                  <Button
                     onClick={(e) =>
                     {
                        e.stopPropagation();
                        panTo(item);
                     }}
                     className="btn-action"
                     icon={<CommonIcons.PantoIcon />}
                  />
               </Tooltip>
            </div>
         </div>
      </div>
   );
};

export default memo(CardSearchResult);
